import React from "react";
import { defaultFirstYear, indicators } from "../constants";
import { createSlug, getDefaultLastYear, toggleSetExistence } from "../utilities";
import {
    convertMaritalStatusToIsInUnion,
    filterResults,
    getPeriod,
    normalizeChartSets,
    normalizeDatabases,
    normalizeEmuData,
    normalizeResults,
    normalizeRuns,
    normalizeSurveyData,
} from "../data";
import * as api from "../api/api";
import fileDownload from "js-file-download";
import { withErrorBoundary } from "../errors";
import CompareRuns from "../pages/CompareRuns";

const createInitialState = () => {
    return {
        loadingData: false,
        databases: undefined,
        runs: undefined,
        selectedRunId: undefined,
        selectedComparisonRunId: undefined,
        selectedCountryNumericCode1: undefined,
        selectedCountryNumericCode2: undefined,
        selectedRegion1: undefined,
        selectedRegion2: undefined,
        period: {
            firstYear: defaultFirstYear,
            lastYear: getDefaultLastYear(),
        },

        surveyData: undefined,
        emuData: undefined,
        results: undefined,
        runTitle: undefined,

        selectedResultsMeasure: "percentage",
        selectedResultsIndicator: indicators[0].value,
        selectedResultsMaritalStatus: "married",
        selectedExportedResultsMeasures: new Set(),
        selectedExportedResultsIndicators: new Set(),
        selectedChartsMeasure: "percentage",
        selectedChartsMaritalStatus: "married",
        selectedChartsIndicators: new Set(
            indicators.filter((indicator) => indicator.chart).map((indicator) => indicator.value)
        ),
        showSurveyLegend: true,
        chartSets: [
            {
                value: "complete",
                label: "Complete",
            },
        ],
        selectedExportedChartSet: "complete",
        selectedExportedChartsMeasure: "percentage",
        selectedExportedChartsMaritalStatus: "married",
    };
};

const CompareRunsContainer = (props) => {
    const [state, setState] = React.useState(createInitialState());

    const signal = api.getSignal();

    const setStateSafely = (newState) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    React.useEffect(() => {
        setStateSafely({
            loadingData: true,
        });

        // noinspection JSCheckFunctionSignatures
        Promise.all([
            api.fetchDatabases(signal.token),
            api.fetchRuns(false, false, signal.token),
            api.fetchChartSets(signal.token),
        ])
            .then((response) => {
                if (!response || response.some((item) => !item)) {
                    return;
                }

                const [databases, runs, chartSets] = response;

                setStateSafely({
                    databases: normalizeDatabases(databases.data),
                    runs: normalizeRuns(runs.data),
                    chartSets: normalizeChartSets(chartSets.data),
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingData: false,
                });
            });

        return () => {
            signal.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCountriesFromRun = (runId) => {
        if (!runId) {
            return {};
        }

        const run = state.runs[runId];
        let runCountries = run.countryNumericCodes;

        if (run.runType === "Subnational") {
            runCountries = [];
        }

        const countryNumericCodes = runCountries;

        const divisions = state.databases[run.surveyDatabaseId]?.divisions;
        if (!divisions) {
            console.error(`No divisions found for ${runId}`);
            return {}; // ERROR
        }

        return countryNumericCodes.reduce((result, countryNumericCode) => {
            if (divisions[countryNumericCode] && divisions[countryNumericCode].isCountry) {
                result[countryNumericCode] = {
                    numericCode: countryNumericCode,
                    ...divisions[countryNumericCode],
                };
            }

            return result;
        }, {});
    };

    const getRegionsFromRun = (runId) => {
        if (!runId) {
            return [];
        }

        const runRegions = state.runs[runId].regionCodes || [];
        return runRegions;
    };

    // const getCountriesIntersection = (runId, comparisonRunId) => {
    //     if (!runId || !comparisonRunId) {
    //         return {};
    //     }

    //     const run = state.runs[runId];
    //     const comparisonRun = state.runs[comparisonRunId];

    //     let runCountries = run.countryNumericCodes;
    //     let comparisonRunCountries = comparisonRun.countryNumericCodes;

    //     if (run.runType === "Subnational") {
    //         runCountries = [];
    //     }

    //     if (comparisonRun.runType === "Subnational") {
    //         comparisonRunCountries = [];
    //     }

    //     const countryNumericCodes = runCountries.filter((countryNumericCode) =>
    //         comparisonRunCountries.includes(countryNumericCode)
    //     );

    //     const divisions = state.databases[run.surveyDatabaseId]?.divisions;
    //     if (!divisions) {
    //         console.error(`No divisions found for ${runId}`);
    //         return {}; // ERROR
    //     }

    //     return countryNumericCodes.reduce((result, countryNumericCode) => {
    //         if (divisions[countryNumericCode] && divisions[countryNumericCode].isCountry) {
    //             result[countryNumericCode] = {
    //                 numericCode: countryNumericCode,
    //                 ...divisions[countryNumericCode],
    //             };
    //         }

    //         return result;
    //     }, {});
    // };

    // const getRegionsIntersection = (runId, comparisonRunId) => {
    //     if (!runId || !comparisonRunId) {
    //         return [];
    //     }

    //     const runRegions = state.runs[runId].regionCodes || [];
    //     const comparisonRunRegions = state.runs[comparisonRunId].regionCodes || [];

    //     return runRegions.filter((region) => comparisonRunRegions.includes(region));
    // };

    const selectDefaultCountryOrRegion1 = (runId) => {
        const countries = getCountriesFromRun(runId);
        const regions = getRegionsFromRun(runId);
        const countryNumericCodes = Object.keys(countries);

        if (countryNumericCodes.length) {
            setStateSafely({
                selectedCountryNumericCode1: parseInt(countryNumericCodes[0], 10),
                selectedRegion1: undefined,
            });
        } else if (regions.length) {
            setStateSafely({
                selectedCountryNumericCode1: undefined,
                selectedRegion1: regions[0],
            });
        } else {
            setStateSafely({
                selectedCountryNumericCode1: undefined,
                selectedRegion1: undefined,
            });
        }
    };

    const selectDefaultCountryOrRegion2 = (comparisonRunId) => {
        const countries = getCountriesFromRun(comparisonRunId);
        const regions = getRegionsFromRun(comparisonRunId);
        const countryNumericCodes = Object.keys(countries);

        if (countryNumericCodes.length) {
            setStateSafely({
                selectedCountryNumericCode2: parseInt(countryNumericCodes[0], 10),
                selectedRegion2: undefined,
            });
        } else if (regions.length) {
            setStateSafely({
                selectedCountryNumericCode2: undefined,
                selectedRegion2: regions[0],
            });
        } else {
            setStateSafely({
                selectedCountryNumericCode2: undefined,
                selectedRegion2: undefined,
            });
        }
    };

    // const selectDefaultCountryOrRegion = (runId, comparisonRunId) => {
    //     // const countryNumericCodes = Object.keys(getCountriesIntersection(runId, comparisonRunId));
    //     // const regions = getRegionsIntersection(runId, comparisonRunId);
    //     // if (countryNumericCodes.length) {
    //     //     setStateSafely({
    //     //         selectedCountryNumericCode1: parseInt(countryNumericCodes[0], 10),
    //     //         selectedCountryNumericCode2: parseInt(countryNumericCodes[0], 10),
    //     //         selectedRegion1: undefined,
    //     //         selectedRegion2: undefined,
    //     //     });
    //     // } else if (regions.length) {
    //     //     setStateSafely({
    //     //         selectedCountryNumericCode1: undefined,
    //     //         selectedCountryNumericCode2: undefined,
    //     //         selectedRegion1: regions[0],
    //     //         selectedRegion2: regions[0],
    //     //     });
    //     // } else {
    //     //     setStateSafely({
    //     //         selectedCountryNumericCode1: undefined,
    //     //         selectedCountryNumericCode2: undefined,
    //     //         selectedRegion1: undefined,
    //     //         selectedRegion2: undefined,
    //     //     });
    //     // }
    // };

    const getSelectedCountryNumericCodeOrRegion1 = () => {
        return state.selectedRegion1 || state.selectedCountryNumericCode1;
    };

    const getSelectedCountryNumericCodeOrRegion2 = () => {
        return state.selectedRegion2 || state.selectedCountryNumericCode2;
    };

    const getCountryRegionName = (runId, selectedCountryNumericCode, selectedRegion) => {
        const selectedRun = state.runs?.[runId];

        if (!selectedRun) {
            return "-";
        }

        const divisions = state.databases?.[selectedRun.surveyDatabaseId]?.divisions;
        const countryName = divisions?.[selectedCountryNumericCode]?.name;
        const countryAlphaCodeOrRegion = countryName || selectedRegion;
        return countryAlphaCodeOrRegion;
    };

    const createRunTitle = () => {
        const { firstYear, lastYear } = state.period;

        const selectedRun = state.runs?.[state.selectedRunId];
        const selectedComparisonRun = state.runs?.[state.selectedComparisonRunId];
        const runName1 = selectedRun?.name;
        const runName2 = selectedComparisonRun?.name;

        const countryOrRegionName1 = getCountryRegionName(
            state.selectedRunId,
            state.selectedCountryNumericCode1,
            state.selectedRegion1
        );
        const countryOrRegionName2 = getCountryRegionName(
            state.selectedComparisonRunId,
            state.selectedCountryNumericCode2,
            state.selectedRegion2
        );

        // const divisions1 = state.databases?.[selectedRun.surveyDatabaseId]?.divisions;
        // const divisions2 = state.databases?.[selectedComparisonRun.surveyDatabaseId]?.divisions;

        // const countryName1 = divisions1?.[state.selectedCountryNumericCode1]?.name; // .alphaCode
        // const countryName2 = divisions2?.[state.selectedCountryNumericCode2]?.name; // .alphaCode

        // const countryAlphaCodeOrRegion1 = countryName1 || state.selectedRegion1;
        // const countryAlphaCodeOrRegion2 = countryName2 || state.selectedRegion2;

        if (countryOrRegionName1 && countryOrRegionName2) {
            return `Run ${runName1} (${countryOrRegionName1}) vs ${runName2} (${countryOrRegionName2}) for during ${firstYear}-${lastYear}`;
        }
    };

    const getMeasureResultsForPeriod = (measure) => {
        if (!state.results) {
            return;
        }

        const measureKey = measure === "percentage" ? "proportions" : "populations";

        return filterResults(state.results[measureKey], state.period.firstYear, state.period.lastYear);
    };

    const getTableResults = () => {
        let measureResultsForPeriod = getMeasureResultsForPeriod(state.selectedResultsMeasure);

        const isInUnion = convertMaritalStatusToIsInUnion(state.selectedResultsMaritalStatus);

        return (
            measureResultsForPeriod &&
            measureResultsForPeriod[state.selectedResultsIndicator].filter((datum) => datum.isInUnion === isInUnion)
        );
    };

    const getChartSurveyData = () => {
        if (!state.surveyData) {
            return;
        }

        const { firstYear, lastYear } = state.period;

        const isInUnion = convertMaritalStatusToIsInUnion(state.selectedChartsMaritalStatus);

        return state.surveyData.filter(
            (datum) => datum.startDate >= firstYear && datum.startDate <= lastYear && datum.isInUnion === isInUnion
        );
    };

    const getChartResults = () => {
        const measureResultsForPeriod = getMeasureResultsForPeriod(state.selectedChartsMeasure);

        if (!measureResultsForPeriod) {
            return;
        }

        const isInUnion = convertMaritalStatusToIsInUnion(state.selectedChartsMaritalStatus);

        const results = Object.keys(measureResultsForPeriod).reduce((result, indicator) => {
            if (state.selectedChartsIndicators.has(indicator)) {
                result[indicator] = measureResultsForPeriod[indicator].filter((datum) => datum.isInUnion === isInUnion);
            }

            return result;
        }, {});

        if (!Object.keys(results).length || !results[Object.keys(results)[0]].length) {
            return;
        }

        return results;
    };

    //------------------------------------------------------------------------------------------------------------------

    const onSelectRun = (runId) => {
        setStateSafely({
            selectedRunId: runId,
        });
        selectDefaultCountryOrRegion1(runId);
    };

    const onSelectComparisonRun = (comparisonRunId) => {
        setStateSafely({
            selectedComparisonRunId: comparisonRunId,
        });
        selectDefaultCountryOrRegion2(comparisonRunId);
    };

    const onSelectCountry1 = (countryNumericCode) => {
        setStateSafely({
            selectedCountryNumericCode1: countryNumericCode,
            selectedRegion1: undefined,
        });
    };

    const onSelectRegion1 = (region) => {
        setStateSafely({
            selectedCountryNumericCode1: undefined,
            selectedRegion1: region,
        });
    };

    const onSelectCountry2 = (countryNumericCode) => {
        setStateSafely({
            selectedCountryNumericCode2: countryNumericCode,
            selectedRegion2: undefined,
        });
    };

    const onSelectRegion2 = (region) => {
        setStateSafely({
            selectedCountryNumericCode2: undefined,
            selectedRegion2: region,
        });
    };

    const onChangePeriod = (period) => {
        setStateSafely({
            period: period,
        });
    };

    const onCompareRuns = () => {
        const run = state.runs[state.selectedRunId];
        const comparisonRun = state.runs[state.selectedComparisonRunId];

        const countryNumericCodeOrRegion1 = getSelectedCountryNumericCodeOrRegion1();
        const countryNumericCodeOrRegion2 = getSelectedCountryNumericCodeOrRegion2();

        setStateSafely({
            loadingRuns: true,
        });

        // noinspection JSCheckFunctionSignatures
        Promise.all([
            api.fetchSurveyDatabase(
                run.surveyDatabaseId,
                countryNumericCodeOrRegion1,
                state.selectedRunId,
                undefined,
                signal.token
            ),
            api.fetchSurveyDatabase(
                comparisonRun.surveyDatabaseId,
                countryNumericCodeOrRegion2,
                state.selectedComparisonRunId,
                undefined,
                signal.token
            ),
            run.emuDatabaseId
                ? api.fetchEmuDatabase(run.emuDatabaseId, countryNumericCodeOrRegion1, signal.token)
                : new Promise((resolve) =>
                      resolve({
                          data: [],
                      })
                  ),
            comparisonRun.emuDatabaseId
                ? api.fetchEmuDatabase(comparisonRun.emuDatabaseId, countryNumericCodeOrRegion2, signal.token)
                : new Promise((resolve) =>
                      resolve({
                          data: [],
                      })
                  ),
            api.fetchResults(
                state.selectedRunId,
                state.selectedComparisonRunId,
                getSelectedCountryNumericCodeOrRegion1(),
                getSelectedCountryNumericCodeOrRegion2(),
                signal.token
            ),
        ])
            .then((response) => {
                if (!response || response.some((item) => !item)) {
                    return;
                }

                const [runSurveyData, comparisonRunSurveyData, runEmuData, comparisonRunEmuData, results] = response;

                const period = getPeriod(results.data.proportions);

                setStateSafely({
                    period: period,
                    surveyData: normalizeSurveyData([...runSurveyData.data, ...comparisonRunSurveyData.data]),
                    emuData: normalizeEmuData([...runEmuData.data, ...comparisonRunEmuData.data]),
                    results: {
                        proportions: normalizeResults(results.data.proportions),
                        populations: normalizeResults(results.data.populations),
                    },
                    runTitle: createRunTitle(),
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingRuns: false,
                });
            });
    };

    const onChangeResultsMeasure = (measure) => {
        setStateSafely({
            selectedResultsMeasure: measure,
            selectedResultsIndicator:
                measure === "percentage" || state.selectedResultsIndicator !== "ratioModernAny"
                    ? state.selectedResultsIndicator
                    : indicators[0].value,
        });
    };

    const onChangeResultsIndicator = (indicator) => {
        setStateSafely({
            selectedResultsIndicator: indicator,
        });
    };

    const onChangeResultsMaritalStatus = (martialStatus) => {
        setStateSafely({
            selectedResultsMaritalStatus: martialStatus,
        });
    };

    const onToggleExportedResultsMeasure = (measure) => {
        const selectedExportedResultsMeasures = new Set(state.selectedExportedResultsMeasures);

        toggleSetExistence(selectedExportedResultsMeasures, measure);

        setStateSafely({
            selectedExportedResultsMeasures,
        });
    };

    const onToggleExportedResultsIndicator = (indicator) => {
        const selectedExportedResultsIndicators = new Set(state.selectedExportedResultsIndicators);

        toggleSetExistence(selectedExportedResultsIndicators, indicator);

        setStateSafely({
            selectedExportedResultsIndicators: selectedExportedResultsIndicators,
        });
    };

    const onDownloadSelectedResults = () => {
        api.downloadResults(
            state.selectedRunId,
            state.selectedComparisonRunId,
            getSelectedCountryNumericCodeOrRegion1(),
            getSelectedCountryNumericCodeOrRegion2(),
            Array.from(state.selectedExportedResultsMeasures),
            Array.from(state.selectedExportedResultsIndicators),
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            const comparisonRun = state.runs[state.selectedComparisonRunId];

            if (run && comparisonRun) {
                const prefix = `${run.name}_vs_${comparisonRun.name}`;

                fileDownload(response.data, createSlug(prefix, "results.csv"));
            }
        });
    };

    const onDownloadAllResults = () => {
        api.downloadResults(
            state.selectedRunId,
            state.selectedComparisonRunId,
            getSelectedCountryNumericCodeOrRegion1(),
            getSelectedCountryNumericCodeOrRegion2(),
            ["percentage", "count"],
            indicators.map((indicator) => indicator.value),
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];
            const comparisonRun = state.runs[state.selectedComparisonRunId];

            if (run && comparisonRun) {
                const prefix = `${run.name}_vs_${comparisonRun.name}`;

                fileDownload(response.data, createSlug(prefix, "all_results.csv"));
            }
        });
    };

    const onChangeChartsMeasure = (measure) => {
        setStateSafely({
            selectedChartsMeasure: measure,
        });
    };

    const onChangeChartsMaritalStatus = (martialStatus) => {
        setStateSafely({
            selectedChartsMaritalStatus: martialStatus,
        });
    };

    const onToggleChartIndicator = (chart) => {
        const selectedChartsIndicators = new Set(state.selectedChartsIndicators);

        toggleSetExistence(selectedChartsIndicators, chart);

        setStateSafely({
            selectedChartsIndicators: selectedChartsIndicators,
        });
    };

    const onToggleSurveyLegend = () => {
        setStateSafely({
            showSurveyLegend: !state.showSurveyLegend,
        });
    };

    const onChangeExportedChartSet = (chartSet) => {
        setStateSafely({
            selectedExportedChartSet: chartSet,
        });
    };

    const onChangeExportedChartsMeasure = (measure) => {
        setStateSafely({
            selectedExportedChartsMeasure: measure,
        });
    };

    const onChangeExportedChartsMaritalStatus = (maritalStatus) => {
        setStateSafely({
            selectedExportedChartsMaritalStatus: maritalStatus,
        });
    };

    const onDownloadChartSet = () => {
        api.downloadChartSet(
            state.selectedRunId,
            state.selectedComparisonRunId,
            getSelectedCountryNumericCodeOrRegion1(),
            getSelectedCountryNumericCodeOrRegion2(),
            state.selectedExportedChartSet,
            state.selectedExportedChartsMaritalStatus,
            state.selectedExportedChartsMeasure,
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            const comparisonRun = state.runs[state.selectedComparisonRunId];

            if (run && comparisonRun) {
                const prefix = `${run.name}_vs_${comparisonRun.name}_${state.selectedExportedChartSet}`;

                fileDownload(response.data, createSlug(prefix, "charts.pdf"));
            }
        });
    };

    const onDownloadSelectedCharts = () => {
        api.downloadCharts(
            state.selectedRunId,
            state.selectedComparisonRunId,
            getSelectedCountryNumericCodeOrRegion1(),
            getSelectedCountryNumericCodeOrRegion2(),
            state.selectedExportedChartsMaritalStatus,
            state.selectedExportedChartsMeasure,
            Array.from(state.selectedChartsIndicators),
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            const comparisonRun = state.runs[state.selectedComparisonRunId];

            if (run && comparisonRun) {
                const prefix = `${run.name}_vs_${comparisonRun.name}`;

                fileDownload(response.data, createSlug(prefix, "charts.pdf"));
            }
        });
    };

    const runType = state.runs?.[state.selectedRunId]?.runType;
    const comparisonRunType = state.runs?.[state.selectedComparisonRunId]?.runType;
    // const countries = getCountriesIntersection(state.selectedRunId, state.selectedComparisonRunId);
    // const regions = getRegionsIntersection(state.selectedRunId, state.selectedComparisonRunId);

    const countries1 = getCountriesFromRun(state.selectedRunId);
    const countries2 = getCountriesFromRun(state.selectedComparisonRunId);
    const regions1 = getRegionsFromRun(state.selectedRunId);
    const regions2 = getRegionsFromRun(state.selectedComparisonRunId);

    const selectedResultsDownloadURL = api.createResultsDownloadUrl(
        state.selectedRunId,
        state.selectedComparisonRunId,
        getSelectedCountryNumericCodeOrRegion1(),
        getSelectedCountryNumericCodeOrRegion2(),
        Array.from(state.selectedExportedResultsMeasures),
        Array.from(state.selectedExportedResultsIndicators)
    );

    const allResultsDownloadURL = api.createResultsDownloadUrl(
        state.selectedRunId,
        state.selectedComparisonRunId,
        getSelectedCountryNumericCodeOrRegion1(),
        getSelectedCountryNumericCodeOrRegion2(),
        ["percentage", "count"],
        indicators.map((indicator) => indicator.value)
    );

    const chartSetDownloadURL = api.createChartSetDownloadUrl(
        state.selectedRunId,
        state.selectedComparisonRunId,
        getSelectedCountryNumericCodeOrRegion1(),
        getSelectedCountryNumericCodeOrRegion2(),
        state.selectedExportedChartSet,
        state.selectedExportedChartsMaritalStatus,
        state.selectedExportedChartsMeasure
    );

    const selectedChartsDownloadURL = api.createChartsDownloadUrl(
        state.selectedRunId,
        state.selectedComparisonRunId,
        getSelectedCountryNumericCodeOrRegion1(),
        getSelectedCountryNumericCodeOrRegion2(),
        state.selectedExportedChartsMaritalStatus,
        state.selectedExportedChartsMeasure,
        Array.from(state.selectedChartsIndicators)
    );

    const countryOrRegionName1 = getCountryRegionName(
        state.selectedRunId,
        state.selectedCountryNumericCode1,
        state.selectedRegion1
    );
    const countryOrRegionName2 = getCountryRegionName(
        state.selectedComparisonRunId,
        state.selectedCountryNumericCode2,
        state.selectedRegion2
    );

    return (
        <CompareRuns
            match={props.match}
            loadingData={state.loadingData}
            runs={state.runs}
            selectedRunId={state.selectedRunId}
            selectedRunName={state.runs && state.selectedRunId && state.runs[state.selectedRunId].name}
            selectedComparisonRunId={state.selectedComparisonRunId}
            selectedComparisonRunName={
                state.runs && state.selectedComparisonRunId && state.runs[state.selectedComparisonRunId].name
            }
            selectedCountryNumericCode1={state.selectedCountryNumericCode1}
            selectedCountryNumericCode2={state.selectedCountryNumericCode2}
            selectedRegion1={state.selectedRegion1}
            selectedRegion2={state.selectedRegion2}
            period={state.period}
            loadingRuns={state.loadingRuns}
            runsTitle={state.runTitle}
            selectedResultsMeasure={state.selectedResultsMeasure}
            selectedResultsIndicator={state.selectedResultsIndicator}
            selectedResultsMaritalStatus={state.selectedResultsMaritalStatus}
            tableResults={getTableResults()}
            selectedExportedResultsMeasures={Array.from(state.selectedExportedResultsMeasures)}
            selectedExportedResultsIndicators={Array.from(state.selectedExportedResultsIndicators)}
            disableSelectedResultsDownload={!selectedResultsDownloadURL}
            disableAllResultsDownload={!allResultsDownloadURL}
            selectedChartsMeasure={state.selectedChartsMeasure}
            selectedChartsMaritalStatus={state.selectedChartsMaritalStatus}
            selectedChartsIndicators={Array.from(state.selectedChartsIndicators)}
            showSurveyLegend={state.showSurveyLegend}
            chartResults={getChartResults()}
            chartSurveyData={getChartSurveyData()}
            chartEmuData={state.selectedChartsMaritalStatus === "married" ? state.emuData : undefined}
            chartSets={state.chartSets}
            selectedExportedChartSet={state.selectedExportedChartSet}
            selectedExportedChartsMeasure={state.selectedExportedChartsMeasure}
            selectedExportedChartsMaritalStatus={state.selectedExportedChartsMaritalStatus}
            disableChartSetDownload={!chartSetDownloadURL}
            disableSelectedChartsDownload={!selectedChartsDownloadURL}
            onSelectRun={onSelectRun}
            onSelectComparisonRun={onSelectComparisonRun}
            onSelectCountry1={onSelectCountry1}
            onSelectRegion1={onSelectRegion1}
            onSelectCountry2={onSelectCountry2}
            onSelectRegion2={onSelectRegion2}
            onChangePeriod={onChangePeriod}
            onCompareRuns={onCompareRuns}
            onChangeResultsMeasure={onChangeResultsMeasure}
            onChangeResultsIndicator={onChangeResultsIndicator}
            onChangeResultsMaritalStatus={onChangeResultsMaritalStatus}
            onToggleExportedResultsMeasure={onToggleExportedResultsMeasure}
            onToggleExportedResultsIndicator={onToggleExportedResultsIndicator}
            onDownloadSelectedResults={onDownloadSelectedResults}
            onDownloadAllResults={onDownloadAllResults}
            onChangeChartsMeasure={onChangeChartsMeasure}
            onChangeChartsMaritalStatus={onChangeChartsMaritalStatus}
            onToggleChartIndicator={onToggleChartIndicator}
            onToggleSurveyLegend={onToggleSurveyLegend}
            onChangeExportedChartSet={onChangeExportedChartSet}
            onChangeExportedChartsMeasure={onChangeExportedChartsMeasure}
            onChangeExportedChartsMaritalStatus={onChangeExportedChartsMaritalStatus}
            onDownloadChartSet={onDownloadChartSet}
            onDownloadSelectedCharts={onDownloadSelectedCharts}
            runType={runType}
            comparisonRunType={comparisonRunType}
            countries1={countries1}
            countries2={countries2}
            regions1={regions1}
            regions2={regions2}
            countryOrRegionName1={countryOrRegionName1}
            countryOrRegionName2={countryOrRegionName2}
        />
    );
};

export default withErrorBoundary(CompareRunsContainer);
